// Per-group Focused conversation identity.
//
// Conversations are kept per interrogation GROUP so switching groups doesn't
// destroy the others (Step 1 deferred this). A group is keyed NOT by its index
// but by the deterministic identity of its record set — so:
//   - a conversation can never be misattributed if batch ordering ever shifts
//     (item 3), and
//   - preserving vs. resetting across a chip edit is intrinsic (item 2): a
//     re-preview that yields the SAME matched set produces the SAME keys
//     (preserve); a changed matched set produces DIFFERENT keys (the old
//     conversations no longer resolve → effectively reset). The caller also
//     compares the whole-set signature and clears the store on change, so
//     stale conversations never linger.
//
// Pure + order-independent + deduped. Unit-tested.
export function recordSetKey(recordIds: readonly string[]): string {
  return [...new Set(recordIds)].sort().join("|");
}

// Signature of the whole matched set — same operation over every matched
// record id. When this changes between /prepare calls, the search changed and
// all per-group conversations must reset (item 2 reset boundary).
export function matchedSetSignature(recordIds: readonly string[]): string {
  return recordSetKey(recordIds);
}

// ---------------------------------------------------------------------------
// "Drill down to cited records" — distil the answer-relevant subset.
//
// The union of the records the answers actually CITED, from two sources,
// deduped by record_id:
//   (a) [rec:ID] tokens parsed from every per-group / drill conversation's
//       assistant text (there are no structured findings for the single-group
//       /answer path — the text is the only citation record), and
//   (b) the STRUCTURED rec_ids of the cross-group reduce result (already
//       citation-gate-validated), preferred over re-parsing the answer text.
// ---------------------------------------------------------------------------
import { citedRecordIds } from "./provenance";

// Deduped union of cited record ids across both sources, first-seen order.
export function distillCitedIds(
  conversationTexts: readonly string[],
  reduceRecIds: readonly string[],
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (id: string) => {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  };
  for (const text of conversationTexts) for (const id of citedRecordIds(text)) add(id);
  for (const id of reduceRecIds) add(id);
  return out;
}

// Narrow the matched set to the cited ids, preserving the matched set's
// (chronological) order. Ids not present in `matched` are dropped, so the
// result is ALWAYS a subset of the matched set — a cited id that isn't a real
// matched record can never enter the drill-down scope.
export function filterToCited<T extends { record_id: string }>(
  matched: readonly T[],
  citedIds: readonly string[],
): T[] {
  const wanted = new Set(citedIds);
  return matched.filter((r) => wanted.has(r.record_id));
}

// ---------------------------------------------------------------------------
// Active-surface scope banner. One banner renders under the conversation tab
// strip for the ACTIVE surface. The three surfaces make semantically DIFFERENT
// "unanswered" claims and must never collapse to a generic line:
//   - group:    not found in THIS GROUP (not the whole matched set)
//   - spanning: not found ANYWHERE in the matched set
//   - drill:    not found among THESE CITED records
// Wording is unchanged from the pre-unification per-surface banners; this is
// the single source of truth for both the render and the test.
// ---------------------------------------------------------------------------
export type ScopeSurface =
  | { kind: "group"; count: number; index: number; total: number }
  | { kind: "drill"; count: number; matchedTotal: number }
  | { kind: "spanning" };

export function scopeBannerText(s: ScopeSurface): string {
  const rec = (n: number) => `${n} record${n === 1 ? "" : "s"}`;
  if (s.kind === "spanning") {
    return (
      "Consolidated across all groups. “Requests with no response found” lists " +
      "requests unanswered anywhere in the matched set. Verify each answer " +
      "against its cited source."
    );
  }
  if (s.kind === "drill") {
    return (
      `These ${rec(s.count)} are the ones cited in your previous answers — not ` +
      `the full matched set (${s.matchedTotal}). Records no answer cited are not ` +
      `included; “unanswered” here means not found among these cited records — ` +
      `not that it doesn’t exist elsewhere in the case. Verify each answer ` +
      `against its cited source.`
    );
  }
  if (s.total > 1) {
    return (
      `This answer is based only on the ${rec(s.count)} in group ${s.index + 1} ` +
      `of ${s.total}. “Unanswered” / “no response” means none was found in this ` +
      `group — not the whole matched set. Ask the other groups too, and verify ` +
      `against each cited source.`
    );
  }
  return (
    `This answer is based only on the ${rec(s.count)} that matched your search — ` +
    `nothing was summarised away. “Unanswered” / “no response” means none was ` +
    `found among these records, not necessarily that none exists anywhere in the ` +
    `case. Verify each answer against its cited source.`
  );
}

// The leading icon per surface (🎯 focused/drill, 🧩 spanning consolidation).
export function scopeBannerIcon(s: ScopeSurface): string {
  return s.kind === "spanning" ? "🧩" : "🎯";
}

// Which non-group tabs the conversation strip shows. The spanning tab appears
// only once a spanning result exists (in-session or restored) — its run entry
// point is an action, never a free-looking tab. The cited tab appears only
// once the cited set is non-empty. Group tabs always render (one per batch).
export function focusedTabVisibility(o: {
  hasSpanning: boolean;
  citedCount: number;
}): { spanning: boolean; cited: boolean } {
  return { spanning: o.hasSpanning, cited: o.citedCount > 0 };
}
